import { z } from 'zod';

// ─── Client-context (onboarding) registry ────────────────────────────
// Owns storage, type and validation for the client-provided onboarding
// record. The external client-context API owns BigQuery system.client_context;
// Nexus keeps its own copy (tenant_client_contexts) after the API accepts a
// create or update. Only Brand Brain and the onboarding flows read that copy;
// other runtime consumers (opportunities-engine, data-sync pipelines) read
// BigQuery directly. Brand Brain owns its own presentation metadata.
//
// Keys are the real BigQuery column names — including the historical
// spellings `bundeling`, `logistic_support`, `product_variance` and
// `engagement` — and are never normalized.

export const CLIENT_CONTEXT_TABLE = 'vertex-poc-447312.system.client_context';

/**
 * Customer setup Data / Manus processes start from. The client-context API
 * writes the setup row once on create; Nexus never writes it, and onboarding
 * reads use the Postgres setup snapshot — see `clients-params.ts`.
 * Onboarding / brand fields are never written here.
 */
export const CLIENTS_PARAMS_TABLE = 'vertex-poc-447312.run_management.clients_params';

/** Legacy core customer partition → competitor brand slug. Omitted when the names match. */
export const CORE_COMP_MAP_TABLE = 'vertex-poc-447312.system.core_comp_map';

/** Dataset location of the three onboarding tables. */
export const ONBOARDING_BQ_LOCATION = 'us-east1';

export type ClientContextValueType = 'string' | 'json' | 'numeric';

export type ClientContextJsonValue =
    | string
    | number
    | boolean
    | null
    | ClientContextJsonValue[]
    | { [key: string]: ClientContextJsonValue };

/** A validated, storage-ready value: text, JSON, or a NUMERIC decimal string. */
export type ClientContextStoredValue = string | ClientContextJsonValue | null;

type FieldSchema = z.ZodType<ClientContextStoredValue, z.ZodTypeDef, unknown>;

/**
 * - `customer-key`: the matching key. Derived from the tenant's
 *   customerPartition, never accepted from an onboarding payload.
 * - `create-only`: written on create, locked on edit.
 * - `editable`: written on create and editable afterwards.
 */
export type ClientContextWriteRule = 'customer-key' | 'create-only' | 'editable';

export interface ClientContextFieldDefinition {
    type: ClientContextValueType;
    write: ClientContextWriteRule;
    schema: FieldSchema;
}

// ─── Validation schemas ──────────────────────────────────────────────

export const CLIENT_CONTEXT_MAX_TEXT_LENGTH = 20_000;
export const CLIENT_CONTEXT_MAX_JSON_LENGTH = 50_000;
export const CLIENT_CONTEXT_MAX_LIST_ITEMS = 200;
export const CLIENT_CONTEXT_MAX_LIST_ITEM_LENGTH = 500;

/** BigQuery NUMERIC: precision 38, scale 9 → at most 29 integer digits. */
const NUMERIC_DECIMAL_PATTERN = /^-?\d{1,29}(\.\d{1,9})?$/;

/** Blank text is stored as NULL, never `''`, so it stays a gap. */
const textSchema: FieldSchema = z
    .string()
    .max(CLIENT_CONTEXT_MAX_TEXT_LENGTH)
    .nullable()
    .transform((value) => value?.trim() || null);

/** Retool's New Customer form guidance for Industry: "Do not use spaces". */
const CLIENT_INDUSTRY_SPACE_PATTERN = /\s/;

/** True when Client Industry, after trimming, still contains whitespace. */
export function hasClientIndustrySpaces(value: string): boolean {
    return CLIENT_INDUSTRY_SPACE_PATTERN.test(value.trim());
}

const industrySchema: FieldSchema = textSchema.refine(
    (value) => typeof value !== 'string' || !hasClientIndustrySpaces(value),
    { message: 'Must not contain spaces' },
);

const websiteUrlSchema: FieldSchema = z
    .string()
    .max(CLIENT_CONTEXT_MAX_TEXT_LENGTH)
    .nullable()
    .transform((value) => value?.trim() || null)
    .refine((value) => value === null || isHttpUrl(value), { message: 'Must be an http(s) URL' });

const jsonValueSchema: z.ZodType<ClientContextJsonValue> = z.lazy(() =>
    z.union([
        z.string(),
        z.number().finite(),
        z.boolean(),
        z.null(),
        z.array(jsonValueSchema),
        z.record(jsonValueSchema),
    ]),
);

/** JSON shapes other than `product_to_focus_on` are unconfirmed by Data, so any JSON value is accepted. */
const jsonSchema: FieldSchema = jsonValueSchema
    .refine((value) => JSON.stringify(value).length <= CLIENT_CONTEXT_MAX_JSON_LENGTH, {
        message: `JSON must serialize to at most ${CLIENT_CONTEXT_MAX_JSON_LENGTH} characters`,
    });

/**
 * `product_to_focus_on` is consumed as a JSON array of strings by set and
 * script generation, which read it from system.client_context, so that shape
 * is enforced. An empty list is stored as NULL. Product sync does not read
 * this column: it reads `product_to_focus_on` from
 * run_management.clients_params.
 */
const stringListSchema: FieldSchema = z
    .array(z.string().trim().min(1).max(CLIENT_CONTEXT_MAX_LIST_ITEM_LENGTH))
    .max(CLIENT_CONTEXT_MAX_LIST_ITEMS)
    .nullable()
    .transform((value) => (value && value.length > 0 ? value : null));

/**
 * Normalized to a decimal string so the value never passes through a float
 * on its way to NUMERIC. No range is enforced — the valid range for
 * `stickrate_threshold` is unconfirmed by Data.
 */
const numericSchema: FieldSchema = z
    .union([z.string().trim(), z.number().finite()])
    .nullable()
    .transform((value) => (value === null || value === '' ? null : String(value)))
    .refine((value) => value === null || NUMERIC_DECIMAL_PATTERN.test(value), {
        message: 'Must be a decimal with at most 29 integer and 9 fractional digits',
    });

/** Browsers' URL parsers accept whitespace in a host (Chromium percent-encodes it) while Node's rejects it, so it is refused up front to keep form and server checks the same. */
const URL_WHITESPACE_PATTERN = /\s/;

export function isHttpUrl(value: string): boolean {
    if (URL_WHITESPACE_PATTERN.test(value)) return false;
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
        return false;
    }
}

// ─── Field builders ──────────────────────────────────────────────────

/** The rule is always passed: an omitted generic widens to the full union and drops the column from the derived types. */
function text<W extends ClientContextWriteRule>(write: W) {
    return { type: 'string', write, schema: textSchema } as const;
}

function json() {
    return { type: 'json', write: 'editable', schema: jsonSchema } as const;
}

// ─── All 32 columns ──────────────────────────────────────────────────

export const CLIENT_CONTEXT_FIELDS = {
    customer_name: text('customer-key'),
    /**
     * Client Industry: entered at onboarding create, read-only afterwards, and
     * also written to `clients_params.industry` in the create setup row. Never
     * derived from or synced with the Comp industries (`tenant_industries`,
     * `tenant_settings.defaultIndustry`). Free text without spaces.
     */
    industry: { type: 'string', write: 'create-only', schema: industrySchema },
    website_url: { type: 'string', write: 'editable', schema: websiteUrlSchema },
    brand: text('editable'),
    premise: text('editable'),
    differentiation: text('editable'),
    challenges: text('editable'),
    focus_area: text('editable'),
    non_focus: text('editable'),
    additional_context_from_client: text('editable'),
    target_audience: json(),
    growth_target: text('editable'),
    product_to_focus_on: { type: 'json', write: 'editable', schema: stringListSchema },
    product_variance: json(),
    best_sellers: json(),
    bundeling: json(),
    product_description: text('editable'),
    product_benefits: json(),
    tone_of_voice: json(),
    messaging_do: text('editable'),
    messaging_dont: text('editable'),
    things_to_avoid: text('editable'),
    content_strategy: text('editable'),
    product_pricing_and_discounts: text('editable'),
    promotions: text('editable'),
    engagement: text('editable'),
    logistic_support: text('editable'),
    known_competitors: json(),
    nemesis: text('editable'),
    who_to_ignore: json(),
    inspiration_industry: text('editable'),
    stickrate_threshold: { type: 'numeric', write: 'editable', schema: numericSchema },
} as const satisfies Record<string, ClientContextFieldDefinition>;

export type ClientContextColumn = keyof typeof CLIENT_CONTEXT_FIELDS;

export type OnboardingCreateColumn = {
    [K in ClientContextColumn]: (typeof CLIENT_CONTEXT_FIELDS)[K]['write'] extends 'customer-key' ? never : K;
}[ClientContextColumn];

export type OnboardingEditColumn = {
    [K in ClientContextColumn]: (typeof CLIENT_CONTEXT_FIELDS)[K]['write'] extends 'editable' ? K : never;
}[ClientContextColumn];

export const CLIENT_CONTEXT_COLUMNS = Object.keys(CLIENT_CONTEXT_FIELDS) as ClientContextColumn[];

export const ONBOARDING_CREATE_COLUMNS = CLIENT_CONTEXT_COLUMNS.filter(
    (column) => CLIENT_CONTEXT_FIELDS[column].write !== 'customer-key',
) as OnboardingCreateColumn[];

export const ONBOARDING_EDIT_COLUMNS = CLIENT_CONTEXT_COLUMNS.filter(
    (column) => CLIENT_CONTEXT_FIELDS[column].write === 'editable',
) as OnboardingEditColumn[];

export function isClientContextColumn(value: string): value is ClientContextColumn {
    return Object.prototype.hasOwnProperty.call(CLIENT_CONTEXT_FIELDS, value);
}
